import { Close } from '@mui/icons-material'
import { Components, Theme } from '@mui/material'

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    neutral: true
    'futureTheme.white': true
    'futureTheme.bronze': true
    'futureTheme.gold': true
    'futureTheme.orange': true
    'futureTheme.lime': true
    'futureTheme.mint': true
    'futureTheme.pink': true
    // TODO - disable once migration complete
    // default: false - or do we map this to neutral?
    // primary: false
    // secondary: false
    // error: false
  }

  interface ChipPropsVariantOverrides {
    'futureTheme.selection': true
    // TODO - disable once migration complete
    // outlined: false
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ChipPropsSizeOverrides {
    // TODO - disable once migration complete
    // small: false
  }
}

const clickableHover = (backgroundColor: string, borderColor: string) => ({
  '&.MuiChip-clickable:hover': { backgroundColor, borderColor },
})

export const MuiChip: Components<Omit<Theme, 'components'>>['MuiChip'] = {
  defaultProps: {
    color: 'neutral',
    size: 'medium',
    // TODO - new icon
    deleteIcon: <Close />,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['futureTheme.radius-sm'],
      padding: theme.spacing(0.5, 2, 0.5, 1),
      height: 24,
      borderStyle: 'solid',
      borderWidth: theme.futureTheme.strokeWidth.default,
    }),
    label: ({ theme }) => ({
      ...theme.typography['futureTheme.small-medium'],
      padding: 0,
      paddingLeft: theme.spacing(1),
    }),
    icon: ({ theme }) => ({
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
      '&.OmuiCountBadge-root': {
        marginLeft: 0,
      },
      '&:not(.OmuiCountBadge-root)': {
        color: 'inherit',
        height: 14,
        width: 14,
      },
    }),
    deleteIcon: ({ theme }) => ({
      fontSize: 18,
      margin: theme.spacing(0.5),
      color: theme.palette.futureTheme.fg.muted,
      '&:hover': {
        color: theme.palette.futureTheme.fg.default,
      },
    }),
  },
  variants: [
    // Selection variant
    {
      props: { variant: 'futureTheme.selection' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
        // Explicit text colour — without it, MUI's built-in styling for the
        // legacy "neutral" palette colour applies its white contrastText
        color: theme.palette.futureTheme.text.default,
        border: 'none',
        padding: theme.spacing(0.5),
        '& .MuiChip-label': {
          ...theme.typography['futureTheme.body-regular'],
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(0.5),
        },
        '&:not(:has(.MuiChip-deleteIcon)) .MuiChip-label': {
          paddingRight: theme.spacing(1.5),
        },
        '&.MuiChip-clickable:hover': {
          backgroundColor: theme.palette.futureTheme.bg.interactive.active,
        },
      }),
    },
    // Colours
    {
      // Migration - use "neutral" as "default" for now. The variant check stops the
      // defaulted color overriding selection chips, which set their own background.
      props: (props) => props.variant !== 'futureTheme.selection' && (props.color === 'neutral' || props.color === 'default'),
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.neutral.muted,
        borderColor: theme.palette.futureTheme.border.subtle,
        color: theme.palette.futureTheme.text.default,
        ...clickableHover(theme.palette.futureTheme.bg.neutral.mutedHover, theme.palette.futureTheme.border.strong),
      }),
    },
    {
      props: { color: 'futureTheme.white' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
        borderColor: theme.palette.futureTheme.border.subtle,
        color: theme.palette.futureTheme.text.default,
        ...clickableHover(theme.palette.futureTheme.bg.surface.elevated, theme.palette.futureTheme.border.strong),
      }),
    },
    {
      props: { color: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.success.muted,
        borderColor: theme.palette.futureTheme.border.success.subtle,
        color: theme.palette.futureTheme.text.success.onSuccessMuted,
        ...clickableHover(theme.palette.futureTheme.bg.success.mutedHover, theme.palette.futureTheme.border.success.strong),
      }),
    },
    {
      props: { color: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.warning.muted,
        borderColor: theme.palette.futureTheme.border.warning.subtle,
        color: theme.palette.futureTheme.text.warning.onWarningMuted,
        ...clickableHover(theme.palette.futureTheme.bg.warning.mutedHover, theme.palette.futureTheme.border.warning.strong),
      }),
    },
    {
      props: { color: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.danger.muted,
        borderColor: theme.palette.futureTheme.border.danger.subtle,
        color: theme.palette.futureTheme.text.danger.onDangerMuted,
        ...clickableHover(theme.palette.futureTheme.bg.danger.mutedHover, theme.palette.futureTheme.border.danger.strong),
      }),
    },
    {
      props: { color: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.info.muted,
        borderColor: theme.palette.futureTheme.border.info.subtle,
        color: theme.palette.futureTheme.text.info.onInfoMuted,
        ...clickableHover(theme.palette.futureTheme.bg.info.mutedHover, theme.palette.futureTheme.border.info.strong),
      }),
    },
    {
      props: { color: 'futureTheme.bronze' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.bronze.muted,
        borderColor: theme.palette.futureTheme.border.utility.bronze.muted,
        color: theme.palette.futureTheme.text.utility.bronze.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.bronze.mutedHover,
          theme.palette.futureTheme.border.utility.bronze.strong
        ),
      }),
    },
    {
      props: { color: 'futureTheme.gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.gold.muted,
        borderColor: theme.palette.futureTheme.border.utility.gold.muted,
        color: theme.palette.futureTheme.text.utility.gold.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.gold.mutedHover,
          theme.palette.futureTheme.border.utility.gold.strong
        ),
      }),
    },
    {
      props: { color: 'futureTheme.orange' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.orange.muted,
        borderColor: theme.palette.futureTheme.border.utility.orange.muted,
        color: theme.palette.futureTheme.text.utility.orange.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.orange.mutedHover,
          theme.palette.futureTheme.border.utility.orange.strong
        ),
      }),
    },
    {
      props: { color: 'futureTheme.lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.lime.muted,
        borderColor: theme.palette.futureTheme.border.utility.lime.muted,
        color: theme.palette.futureTheme.text.utility.lime.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.lime.mutedHover,
          theme.palette.futureTheme.border.utility.lime.strong
        ),
      }),
    },
    {
      props: { color: 'futureTheme.mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.mint.muted,
        borderColor: theme.palette.futureTheme.border.utility.mint.muted,
        color: theme.palette.futureTheme.text.utility.mint.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.mint.mutedHover,
          theme.palette.futureTheme.border.utility.mint.strong
        ),
      }),
    },
    {
      props: { color: 'futureTheme.pink' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.pink.muted,
        borderColor: theme.palette.futureTheme.border.utility.pink.muted,
        color: theme.palette.futureTheme.text.utility.pink.default,
        ...clickableHover(
          theme.palette.futureTheme.bg.utility.pink.mutedHover,
          theme.palette.futureTheme.border.utility.pink.strong
        ),
      }),
    },
  ],
}
